import { Lock, Check, Loader2, School, User } from "lucide-react";
import { useT } from "@/i18n";

export const Paywall = ({ onUnlock, loadingKey }) => {
  const { t } = useT();
  const plans = [
    {
      lookup: "ionara_pro",
      icon: User,
      name: t("paywall.indName"),
      price: "$9.99",
      unit: t("paywall.indUnit"),
      tagline: t("paywall.indTag"),
      features: t("paywall.indF"),
      cta: t("paywall.indCta"),
      highlight: false,
    },
    {
      lookup: "ionara_classroom",
      icon: School,
      name: t("paywall.className"),
      price: "$199",
      unit: t("paywall.classUnit"),
      tagline: t("paywall.classTag"),
      features: t("paywall.classF"),
      cta: t("paywall.classCta"),
      highlight: true,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8" data-testid="paywall">
      <div className="max-w-2xl mb-8">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
          {t("paywall.h2")} <span className="text-accent">{t("paywall.everything")}</span>
        </h2>
        <p className="mt-4 text-base text-background/70 leading-relaxed">{t("paywall.intro")}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
        {plans.map((p) => {
          const Icon = p.icon;
          const busy = loadingKey === p.lookup;
          return (
            <div
              key={p.lookup}
              className={`brutal-border rounded-2xl p-8 ${
                p.highlight ? "bg-accent text-accent-foreground" : "bg-background text-foreground"
              }`}
              style={{
                boxShadow: p.highlight
                  ? "6px 6px 0 0 rgba(10,10,10,1)"
                  : "6px 6px 0 0 rgba(253,251,247,0.9)",
              }}
              data-testid={`paywall-${p.lookup}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-5 h-5" />
                <span className="step-tag opacity-70">{p.name.toUpperCase()}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display font-extrabold text-5xl tracking-tight">{p.price}</span>
              </div>
              <p className="text-xs font-semibold opacity-70 mt-1">{p.unit}</p>
              <p className="text-sm mt-3 leading-relaxed opacity-90">{p.tagline}</p>

              <ul className="mt-5 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <span
                      className={`mt-0.5 shrink-0 w-5 h-5 rounded-full grid place-items-center ${
                        p.highlight ? "bg-accent-foreground" : "bg-accent"
                      }`}
                    >
                      <Check className={`w-3.5 h-3.5 ${p.highlight ? "text-accent" : "text-accent-foreground"}`} />
                    </span>
                    <span className="font-medium">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`unlock-btn-${p.lookup}`}
                onClick={() => onUnlock(p.lookup)}
                disabled={!!loadingKey}
                className={`mt-7 w-full font-display font-bold px-6 py-4 rounded-full brutal-border transition-all flex items-center justify-center gap-2 disabled:opacity-70 ${
                  p.highlight
                    ? "bg-foreground text-background hover:-translate-y-0.5"
                    : "bg-primary text-primary-foreground hover:-translate-y-0.5"
                }`}
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> …
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" /> {p.cta}
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-background/50">{t("paywall.secure")}</p>
    </div>
  );
};
